import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";
import { Flame, ChevronLeft, ChevronRight, ArrowRight, BadgeCheck, TrendingDown, Truck, Eye, Sparkles } from "lucide-react";
import { highStreetSavings } from "@/lib/highStreetSavings";
import { shippingETA } from "@/lib/shippingETA";
import { socialProofBadge } from "@/lib/socialProof";

/**
 * TrendingCarousel — surfaces the curated `is_trending=true` catalogue
 * above-the-fold on the Landing page. Horizontal scroll with arrow buttons,
 * snap points, and a compact card design optimised for impulse-browsing.
 * Personalised by visitor country + recent browse affinity (session id).
 */
const TrendingCarousel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollerRef = useRef(null);
  const { formatPriceDual, formatPrice, userCountry } = useCurrency();
  const { user } = useAuth();

  useEffect(() => {
    // Build a stable session id so personalisation works for guests too.
    let sid = null;
    try {
      sid = localStorage.getItem("afri_sid");
      if (!sid) {
        sid = `s_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
        localStorage.setItem("afri_sid", sid);
      }
    } catch (_) { /* ignore localStorage failures */ }

    const params = { limit: 24 };
    const country = user?.country || userCountry;
    if (country) params.country = country;
    if (sid) params.session_id = sid;

    api
      .get("/listings/trending", { params })
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [user, userCountry]);

  const scrollBy = (dx) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dx, behavior: "smooth" });
  };

  if (!loading && items.length === 0) return null;

  return (
    <section
      className="relative py-12 sm:py-14 bg-gradient-to-b from-[#FAF8F5] to-white border-y border-black/5"
      data-testid="trending-section"
    >
      <div className="container-narrow">
        <div className="flex items-end justify-between gap-3 mb-5">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#C05A3C]/10 text-[#C05A3C] text-[11px] font-bold uppercase tracking-wider">
                <Flame className="w-3 h-3" /> Trending now
              </span>
              <span className="text-[11px] text-muted-foreground">Hand-picked by AfriMarket</span>
            </div>
            <h2 className="font-display text-2xl sm:text-3xl tracking-tight">
              What the diaspora is loving this week
            </h2>
            <p className="text-sm text-muted-foreground mt-1 font-sans-ui max-w-md">
              From hair to heritage — fast-shipping to UK · US · Canada.
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-1">
            <button
              type="button"
              aria-label="Scroll left"
              onClick={() => scrollBy(-560)}
              className="w-9 h-9 rounded-full border border-black/15 bg-white hover:bg-[#FAF8F5] flex items-center justify-center transition"
              data-testid="trending-scroll-left"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              aria-label="Scroll right"
              onClick={() => scrollBy(560)}
              className="w-9 h-9 rounded-full border border-black/15 bg-white hover:bg-[#FAF8F5] flex items-center justify-center transition"
              data-testid="trending-scroll-right"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="flex gap-4 overflow-x-auto pb-3 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x snap-mandatory scroll-smooth no-scrollbar"
          data-testid="trending-row"
          style={{ scrollbarWidth: "none" }}
        >
          {(loading ? Array.from({ length: 6 }) : items).map((it, i) =>
            loading ? (
              <div
                key={`skel-${i}`}
                className="snap-start shrink-0 w-[180px] sm:w-[200px] rounded-xl bg-white border border-black/5 animate-pulse"
                style={{ height: 290 }}
              />
            ) : (
              <TrendingCard key={it.listing_id} listing={it} formatPriceDual={formatPriceDual} formatPrice={formatPrice} index={i} />
            )
          )}

          {/* "See all" tail card */}
          {!loading && items.length > 0 && (
            <Link
              to="/browse?country=United+Kingdom"
              data-testid="trending-see-all"
              className="snap-start shrink-0 w-[180px] sm:w-[200px] rounded-xl border-2 border-dashed border-[#C05A3C]/40 bg-white hover:bg-[#C05A3C]/5 transition flex flex-col items-center justify-center text-center p-5"
            >
              <ArrowRight className="w-6 h-6 text-[#C05A3C] mb-2" />
              <div className="font-display text-base text-[#1A2024]">See all trending</div>
              <div className="text-[11px] text-muted-foreground mt-1">{items.length}+ curated picks</div>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};

const TrendingCard = ({ listing, formatPriceDual, formatPrice, index }) => {
  const img =
    listing.images?.[0] ||
    "https://images.unsplash.com/photo-1606744824163-985d376605aa?auto=format&fit=crop&q=80&w=600";
  const dual = formatPriceDual(listing.price, listing.currency);
  const price = dual.primary || `${listing.currency || ""} ${listing.price}`;
  // Curated catalogue → safe to surface a UK-high-street savings benchmark.
  const savings = highStreetSavings(listing.price, listing.category, {
    onlyIfCurated: true,
    is_curated: listing.is_curated,
  });
  const savingsLabel = savings
    ? (formatPrice(savings.savings, listing.currency) || `${savings.savings.toFixed(0)}`)
    : null;
  const eta = shippingETA(listing, { onlyIfCurated: true });
  const proof = socialProofBadge(listing);
  const ProofIcon = proof && (proof.icon === "flame" ? Flame : proof.icon === "eye" ? Eye : Sparkles);
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.04, 0.5) }}
      className="snap-start shrink-0 w-[180px] sm:w-[200px]"
    >
      <Link
        to={`/listing/${listing.listing_id}`}
        data-testid={`trending-card-${listing.listing_id}`}
        className="group block rounded-xl bg-white border border-black/10 overflow-hidden hover:border-[#C05A3C]/40 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
      >
        <div className="relative aspect-square overflow-hidden bg-[#F3EFE9]">
          <img
            src={img}
            alt={listing.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          {listing.is_official && (
            <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#1A2024]/90 text-[#D9923B] text-[10px] font-semibold backdrop-blur-sm">
              <BadgeCheck className="w-2.5 h-2.5" /> Official
            </div>
          )}
          <div className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#C05A3C] text-white text-[10px] font-semibold">
            <Flame className="w-2.5 h-2.5" /> Hot
          </div>
        </div>
        <div className="p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{listing.category}</div>
          <div className="text-sm leading-snug font-medium line-clamp-2 min-h-[2.5rem] text-[#1A2024]">
            {listing.title}
          </div>
          <div className="font-display text-lg text-[#C05A3C] mt-2" data-testid={`trending-price-${listing.listing_id}`}>{price}</div>
          {dual.isConverted && dual.original && (
            <div className="text-[10px] text-muted-foreground -mt-0.5" data-testid={`trending-orig-${listing.listing_id}`}>
              listed at {dual.original}
            </div>
          )}
          {savings && (
            <div
              className="mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-[#4A5D23]/12 text-[#3A4A1C] text-[10px] font-semibold"
              data-testid={`trending-savings-${listing.listing_id}`}
              title={`Typical UK high-street price ~${savings.percent}% higher`}
            >
              <TrendingDown className="w-2.5 h-2.5" /> Save {savingsLabel} vs UK retail
            </div>
          )}
          {eta && (
            <div
              className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-[#2A3D36]"
              data-testid={`trending-eta-${listing.listing_id}`}
              title="Typical delivery window to UK · US · Canada"
            >
              <Truck className="w-2.5 h-2.5" /> Ships in {eta.label}
            </div>
          )}
          {proof && (
            <div
              className={
                "mt-1 inline-flex items-center gap-1 text-[10px] font-semibold " +
                (proof.kind === "sold"
                  ? "text-[#C05A3C]"
                  : proof.kind === "views"
                  ? "text-[#1A2024]"
                  : "text-[#4A5D23]")
              }
              data-testid={`trending-proof-${listing.listing_id}`}
            >
              <ProofIcon className="w-2.5 h-2.5" /> {proof.label}
            </div>
          )}
        </div>
      </Link>
    </motion.div>
  );
};

export default TrendingCarousel;
